import React, { useState, useEffect } from "react";
import { Switch, BrowserRouter, withRouter } from "react-router-dom";
import StylesProvider from "@mui/styles/StylesProvider";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditorMainPage from "./Pages/EditorMainPage";
import FroalaInstanceSample from "./Pages/ContentStudio/FroalaInstanceSample";
import BannerManager from "./Pages/Banners/BannerManager";
import LandingPage from "./Pages/TemplateManager/LandingPage";
import Notfound from "./Pages/UrlNotfound";
import { getSession } from "./Uitls";
import {
  PasswordChangeSuccess,
  PasswordChangeFailure,
} from "./components/toastify";
import "./App.scss";
import "./common.scss";
import ErrComponentToRender from "./Uitls/apiErrorHandling";
import { ValueProvider } from "./ContextAPi/appContext";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import KeyCloakPrivateRoute from "./keycloak/KeyCloakPrivateRoute";
import { eventLogger, keycloak, tokenLogger } from "./keycloak/keyCloakConfig";
import PreviewBanner from "./Pages/Banners/PreviewBanner";
import BackupImageSettings from "./Pages/Banners/BackupImageSettings";
import { redirectUri } from "./services/axiosConfigCS";
import {
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
} from "@mui/material/styles";
import { fetchCustomFontsFromApi } from "./services/customFontsApi";
import { useDisableIconDrag } from "./Editor/hooks";

const TOAST_AUTO_CLOSE_POLICY_MS = {
  success: { default: 3000, min: 2000, max: 4000 },
  info: { default: 5000, min: 4000, max: 6000 },
  default: { default: 5000, min: 4000, max: 6000 },
  warning: { default: 8000, min: 6000, max: 10000 },
  error: { default: 8000, min: 6000, max: 10000 },
  actionable: { default: 7000, min: 5000, max: 10000 },
};

const clampDuration = (value, min, max) => Math.min(max, Math.max(min, value));

const getToastAutoClosePolicy = (type, options = {}) => {
  const normalizedType = String(type || "default").toLowerCase();
  const basePolicy =
    TOAST_AUTO_CLOSE_POLICY_MS[normalizedType] ||
    TOAST_AUTO_CLOSE_POLICY_MS.default;

  if (!options?.actionable) return basePolicy;

  return {
    default: Math.max(
      basePolicy.default,
      TOAST_AUTO_CLOSE_POLICY_MS.actionable.default,
    ),
    min: Math.max(basePolicy.min, TOAST_AUTO_CLOSE_POLICY_MS.actionable.min),
    max: Math.max(basePolicy.max, TOAST_AUTO_CLOSE_POLICY_MS.actionable.max),
  };
};

const getToastAutoCloseDuration = (type, options = {}) => {
  const policy = getToastAutoClosePolicy(type, options);
  const autoCloseValue = options?.autoClose;

  if (autoCloseValue === false) {
    return false;
  }

  if (autoCloseValue === true || autoCloseValue == null) {
    return policy.default;
  }

  if (typeof autoCloseValue === "number" && Number.isFinite(autoCloseValue)) {
    return clampDuration(autoCloseValue, policy.min, policy.max);
  }

  return policy.default;
};

const wrapToastMethodWithDuration = (toastMethod, type) => {
  return (content, options = {}) => {
    const normalizedOptions = { ...(options || {}) };
    const actionable = Boolean(normalizedOptions?.actionable);

    // Custom internal flag used for duration selection.
    delete normalizedOptions.actionable;
    normalizedOptions.autoClose = getToastAutoCloseDuration(type, {
      ...normalizedOptions,
      actionable,
    });

    return toastMethod(content, normalizedOptions);
  };
};

const applyGlobalToastAutoCloseDurations = () => {
  const alreadyAppliedFlag = "__csToastDurationsConfigured";
  if (toast[alreadyAppliedFlag]) return;

  if (typeof toast.success === "function") {
    toast.success = wrapToastMethodWithDuration(toast.success, "success");
  }

  if (typeof toast.info === "function") {
    toast.info = wrapToastMethodWithDuration(toast.info, "info");
  }

  if (typeof toast.error === "function") {
    toast.error = wrapToastMethodWithDuration(toast.error, "error");
  }

  if (typeof toast.warn === "function") {
    const wrappedWarn = wrapToastMethodWithDuration(toast.warn, "warning");
    toast.warn = wrappedWarn;
    if (typeof toast.warning === "function") {
      toast.warning = wrappedWarn;
    }
  } else if (typeof toast.warning === "function") {
    toast.warning = wrapToastMethodWithDuration(toast.warning, "warning");
  }

  toast[alreadyAppliedFlag] = true;
};

applyGlobalToastAutoCloseDurations();
export const notify = (id, status, type, assetname) => {
  if (type === "success") {
    toast.success(
      <PasswordChangeSuccess
        id={id}
        status="Success"
        assetname="Password Change Successful"
      />,
    );
  } else if (type === "error") {
    toast.error(
      <PasswordChangeFailure
        id={id}
        status="Failure"
        assetname="Incorrect OTP entered"
      />,
      { autoClose: 2000 },
    );
  }
};

export const apiAuthError = (errorCode) => {
  getSession()
    .then(() => {
      const errCoponent = ErrComponentToRender(errorCode);
      toast.error(errCoponent);
    })
    .catch((err) => {});
};

function App() {
  const [googleFontsLink, setGoogleFontsLink] = useState("");
  const [banner, setBanner] = useState(true);
  const [bannerName, setbannerName] = useState("");
  const [bannerType, setbannerType] = useState("");
  const [newBanner, setnewBanner] = useState(true);
  const [genericInfo, setGenricInfo] = useState({});
  const [brandId, setBrandId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [marketCode, setMarketCode] = useState("");
  const [attributes, setAttributes] = useState({});
  const [assetInfo, setAssetInfo] = useState({
    assetId: "",
    assetName: "",
  });
  const [cardData, setCardData] = useState({
    cards: "",
    search: "recent",
  });
  const theme = createTheme({
    components: {
      MuiTextField: {
        defaultProps: {
          variant: "standard",
        },
      },
      MuiSelect: {
        defaultProps: {
          variant: "standard",
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: "standard",
        },
      },
    },
  });

  const [moduleCardData, setModuleCardData] = useState({
    cards: "",
    search: "recent",
  });
  const [bannersEditCardData, setBannersEditCardData] = useState({
    cards: "",
  });

  const [flagEditBanner, setFlagEditBanner] = useState(false);
  const [flagEditFlow, setFlagEditFlow] = useState(false);
  const [activeCards, setActiveCards] = useState([]);
  const [veevaModulesLinked, setVeevaModulesLinked] = useState(false);
  const [saveCanvasDataOnSessionExpire, setSaveCanvasDataOnSessionExpire] =
    useState(false);

  /** Custom fonts for the inline editor (Froala); loaded from GET /api/v1/custom-fonts. */
  const [savedCustomFontList, setSavedCustomFontList] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadCustomFonts = async () => {
      try {
        const fonts = await fetchCustomFontsFromApi({ assetInfo });
        if (!cancelled) {
          setSavedCustomFontList(fonts);
        }
      } catch (error) {
        console.warn("Could not load custom fonts from API", error);
        if (!cancelled) {
          setSavedCustomFontList([]);
        }
      }
    };

    if (keycloak?.authenticated && assetInfo?.assetId) {
      loadCustomFonts();
    } else if (!cancelled) {
      setSavedCustomFontList([]);
    }

    return () => {
      cancelled = true;
    };
  }, [keycloak?.authenticated, assetInfo.assetId, assetInfo.assetName]);

  const updateGenericInfo = (modalValues) => {
    setGenricInfo({ modalValues });
  };
  const [customTempSize, setCustomTempSize] = useState(false);
  const [moduleExpired, setModuleExpired] = useState(false);
  const [dimensionObj, setDimensionObj] = useState({
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    targetId: "",
    top: 0,
    width: 0,
    x: 0,
    y: 0,
  });
  useEffect(() => {
    const expired = localStorage.getItem("sessionExpired");

    if (expired) {
      const parsed = JSON.parse(expired);

      const refreshTokenExp = parsed?.refreshTokenExp;

      // Current time in seconds
      const currentTime = Math.floor(Date.now() / 1000);

      // If refresh token already expired
      if (refreshTokenExp && currentTime > refreshTokenExp) {

        localStorage.removeItem("sessionExpired");

        return;
      }
      if (!refreshTokenExp) {
        localStorage.removeItem("sessionExpired");
        return;
      }


      localStorage.removeItem("sessionExpired");
      keycloak.logout({
        redirectUri,
      });
    }
  }, [keycloak?.authenticated]);

  useDisableIconDrag()
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <ValueProvider
          value={{
            genericInfo: genericInfo.modalValues,
            brandId: brandId,
            cardData: cardData,
            setCardData: setCardData,
            updateGenericInfo: updateGenericInfo,
            setBrandId: setBrandId,
            campaignId: campaignId,
            setCampaignId: setCampaignId,
            marketCode: marketCode,
            setMarketCode: setMarketCode,
            assetInfo: assetInfo,
            setAssetInfo: setAssetInfo,
            dimensionObj: dimensionObj,
            setDimensionObj: setDimensionObj,
            bannersEditCardData: bannersEditCardData,
            setBannersEditCardData: setBannersEditCardData,
            flagEditBanner: flagEditBanner,
            setFlagEditBanner: setFlagEditBanner,
            flagEditFlow: flagEditFlow,
            setFlagEditFlow: setFlagEditFlow,
            moduleCardData: moduleCardData,
            setModuleCardData: setModuleCardData,
            activeCards: activeCards,
            setActiveCards: setActiveCards,
            googleFontsLink: googleFontsLink,
            setGoogleFontsLink: setGoogleFontsLink,
            veevaModulesLinked: veevaModulesLinked,
            setVeevaModulesLinked: setVeevaModulesLinked,
            banner: banner,
            setBanner: setBanner,
            customTempSize: customTempSize,
            setCustomTempSize: setCustomTempSize,
            attributes: attributes,
            setAttributes: setAttributes,
            setbannerName: setbannerName,
            setbannerType: setbannerType,
            bannerType: bannerType,
            bannerName: bannerName,
            newBanner: newBanner,
            setnewBanner: setnewBanner,
            moduleExpired: moduleExpired,
            setModuleExpired: setModuleExpired,
            saveCanvasDataOnSessionExpire: saveCanvasDataOnSessionExpire,
            setSaveCanvasDataOnSessionExpire: setSaveCanvasDataOnSessionExpire,
            savedCustomFontList: savedCustomFontList,
            setSavedCustomFontList: setSavedCustomFontList,
          }}
        >
          <div className="App">
            <StylesProvider injectFirst>
              <ToastContainer
                className="global-toast-container"
                toastClassName="global-toast"
                bodyClassName="global-toast-body"
                progressClassName="global-toast-progress"
                hideProgressBar={false}
                closeButton={false}
                icon={false}
              />
              <BrowserRouter
                getUserConfirmation={() => {
                  /* Empty callback to block the default browser prompt */
                }}
              >
                <ReactKeycloakProvider
                  initOptions={{ onLoad: "login-required" }}
                  authClient={keycloak}
                  autoRefreshToken={false}
                  onEvent={eventLogger}
                  onTokens={tokenLogger}
                  keycloak={keycloak}
                >
                  <Switch>
                    <KeyCloakPrivateRoute
                      path="/backup-image-settings/:bannerId"
                      component={BackupImageSettings}
                    />
                    <KeyCloakPrivateRoute
                      path="/banner-editor/:bannerId"
                      component={EditorMainPage}
                      editorRoute={true}
                    />
                    <KeyCloakPrivateRoute
                      path="/banner-preview/:bannerId"
                      component={PreviewBanner}
                    />
                    <KeyCloakPrivateRoute
                      path="/froala-editor"
                      component={FroalaInstanceSample}
                    />
                    <KeyCloakPrivateRoute
                      path="/"
                      component={LandingPage}
                      exact
                    />
                    <KeyCloakPrivateRoute
                      path="/webaction"
                      component={LandingPage}
                    />
                    <KeyCloakPrivateRoute
                      path="/bannerManager"
                      component={BannerManager}
                    />
                    <KeyCloakPrivateRoute component={Notfound} />
                  </Switch>
                </ReactKeycloakProvider>
              </BrowserRouter>
            </StylesProvider>
          </div>
        </ValueProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
export default withRouter(App);
